import type {
    InboxWorkflowCellComponent,
    InboxWorkflowFlowBehavior,
    InboxWorkflowFlowConfig,
} from "@kontextwerk/control-web-frame/shared/logic/inbox-workflow"
import {
  withHelp,
  workflowDefinition,
  workflowStep,
} from "../../_shared/_logic/registry-composition"
import { inboxWorkflowHelpDialogIds } from "../../_shared/_logic/help-dialog-ids"
import LetterDeliveryManualDownloadStep from "./steps/03_deliver_appointment_letters"
import {
  createLetterDeliveryDecisionStep,
  letterDeliveryDecisionCells,
  type LetterDeliveryDecisionStepOptions,
} from "./steps/02_decide_appointment_letter_delivery"
import {
  createLetterSelectionStep,
  letterSelectionCells,
} from "./steps/01_select_appointment_letters_for_delivery"
import { manualDeliveryItemIdsFromDecisionResultRows } from "./_logic/manualDeliveryItemIds"

export const letterDeliveryCells: Record<string, InboxWorkflowCellComponent> = {
  ...letterSelectionCells,
  ...letterDeliveryDecisionCells,
}

const printStepIndex = (workflow: { flow: { steps: Array<{ step_key: string }> } }) =>
  workflow.flow.steps.findIndex((step) => step.step_key === "deliver_appointment_letters")

const letterDeliveryBehavior: InboxWorkflowFlowBehavior = {
  resolveActiveIndexAfterLoad: ({ workflow, readOnly, defaultIndex }) => {
    if (readOnly || workflow.flow.flow_key !== "letter_delivery_batch") {
      return defaultIndex
    }

    if (defaultIndex < workflow.flow.steps.length) return defaultIndex

    const index = printStepIndex(workflow)

    return index >= 0 ? index : defaultIndex
  },
  afterCompleted: ({ workflow, result, defaultIndex }) => {
    if (workflow.flow.flow_key !== "letter_delivery_batch" || result.failed_count > 0) {
      return defaultIndex
    }

    if (result.step_key !== "decide_appointment_letter_delivery") return defaultIndex

    if (manualDeliveryItemIdsFromDecisionResultRows(result.rows).length === 0) {
      return workflow.flow.steps.length
    }

    const index = printStepIndex(workflow)

    return index >= 0 ? index : defaultIndex
  },
}

export const createLetterDeliveryBatchFlow = (
  options: LetterDeliveryDecisionStepOptions = {},
): InboxWorkflowFlowConfig => ({
  definition: workflowDefinition({
    flowKey: "letter_delivery_batch",
    label: "Terminbriefe zustellen",
    subjectPolicy: "REQUIRED",
    requirements: [
      "SELECT_APPOINTMENT_LETTERS_FOR_DELIVERY",
      "DECIDE_APPOINTMENT_LETTER_DELIVERY",
      "DELIVER_APPOINTMENT_LETTERS",
    ],
    steps: [
      workflowStep({
        stepKey: "select_appointment_letters_for_delivery",
        label: "Briefauswahl",
        componentKey: "letter_delivery_batch.deliver_appointment_letters",
        requirements: ["SELECT_APPOINTMENT_LETTERS_FOR_DELIVERY"],
        outputKey: "letter_selection",
      }),
      workflowStep({
        stepKey: "decide_appointment_letter_delivery",
        label: "Briefzustellung",
        componentKey: "letter_delivery_batch.deliver_appointment_letters",
        requirements: ["DECIDE_APPOINTMENT_LETTER_DELIVERY"],
        outputKey: "letter_delivery_decision",
      }),
      workflowStep({
        stepKey: "deliver_appointment_letters",
        label: "Drucken",
        componentKey: "letter_delivery_batch.deliver_appointment_letters",
        requirements: ["DELIVER_APPOINTMENT_LETTERS"],
        outputKey: "letter_delivery_completion",
      }),
    ],
  }),
  steps: {
    select_appointment_letters_for_delivery: withHelp(
      createLetterSelectionStep(),
      inboxWorkflowHelpDialogIds.letterDeliverySelectAppointmentLetters,
    ),
    decide_appointment_letter_delivery: withHelp(
      createLetterDeliveryDecisionStep(options),
      inboxWorkflowHelpDialogIds.letterDeliveryDecideAppointmentLetterDelivery,
    ),
    deliver_appointment_letters: withHelp(
      {
        kind: "custom",
        component: LetterDeliveryManualDownloadStep,
      },
      inboxWorkflowHelpDialogIds.letterDeliveryDeliverAppointmentLetters,
    ),
  },
  behavior: letterDeliveryBehavior,
})

export const letterDeliveryBatchFlow: InboxWorkflowFlowConfig = createLetterDeliveryBatchFlow()
